import { useCallback, useEffect, useRef } from 'react';
import type { QuoteComposerContext } from '../../types';

/**
 * Tracks the signing link of the quote a composer session is sending, for the
 * body-replacement handlers to re-assert on AI- or template-generated bodies.
 *
 * The link is seeded from prepare-send and re-seeded whenever the composer
 * switches to a different quote, so a link belonging to an earlier quote never
 * carries over. `handleSigningUrlResolved` refreshes it from the AI generation
 * response, which can resolve a link that did not yet exist when the composer
 * opened.
 *
 * @example
 * ```tsx
 * const { getSigningUrl, handleSigningUrlResolved } = useQuoteSigningLink(quoteContext);
 * setBodySerializedState(ensureSigningLink(body, getSigningUrl()));
 * ```
 */
export function useQuoteSigningLink(quoteContext?: QuoteComposerContext) {
  const quoteId = quoteContext?.quoteId ?? null;
  const signingUrl = quoteContext?.signingUrl ?? null;

  const signingLinkRef = useRef<{ quoteId: string | null; url: string | null }>({
    quoteId,
    url: signingUrl,
  });

  useEffect(() => {
    signingLinkRef.current = { quoteId, url: signingUrl };
  }, [quoteId, signingUrl]);

  const getSigningUrl = useCallback(() => signingLinkRef.current.url, []);

  const handleSigningUrlResolved = useCallback(
    (resolvedSigningUrl: string, resolvedQuoteId?: string) => {
      // A link resolved for a quote the composer has since moved off must not
      // overwrite the current quote's link.
      if ((resolvedQuoteId ?? null) !== signingLinkRef.current.quoteId) return;
      signingLinkRef.current = { ...signingLinkRef.current, url: resolvedSigningUrl };
    },
    []
  );

  return { getSigningUrl, handleSigningUrlResolved };
}
